import {
  Directive,
  Input,
  ElementRef,
  OnDestroy,
  Renderer2,
  AfterViewInit,
  Output,
  EventEmitter,
} from "@angular/core";
import { v4 } from "uuid";
import { fromEvent, filter, Subscription } from "rxjs";
import { createPopper, Instance as PopperInstance, Placement } from "@popperjs/core";
import { MedPopupService } from "../services/med-popup.service";
import { DomUtil } from "../utils/dom.util";

@Directive({
  selector: "[medPopup]",
})
export class MedPopupDirective<InputType, OutputType> implements AfterViewInit, OnDestroy {
  constructor(
    private medPopup: MedPopupService<InputType, OutputType>,
    private el: ElementRef,
    private renderer: Renderer2,
  ) {
    this._hash = `${this.componentKey}-${v4()}`;
  }

  private _hash: string;
  private _clickOutside$?: Subscription;
  private _popperContent$?: Subscription;
  private _popperInstance?: PopperInstance;

  @Input() placement: Placement = "auto";
  @Input("medPopup") componentKey = "";
  @Input() medPopupData?: InputType = undefined;
  @Input() medPopupStyleClass?: string;

  @Output() medPopupClose: EventEmitter<OutputType> = new EventEmitter();

  ngAfterViewInit() {
    if (this.medPopupStyleClass) {
      this.medPopup.popupContentEl.classList.add(this.medPopupStyleClass);
    }

    this._popperInstance = createPopper(this.el.nativeElement, this.medPopup.popupContentEl, {
      strategy: "fixed",
      placement: this.placement,
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [5, 5],
          },
        },
      ],
    });

    this.renderer.setStyle(this.medPopup.popupContentEl, "display", "none");

    fromEvent(this.el.nativeElement, "click")
      .pipe(filter(() => this._popperInstance !== null))
      .subscribe(() => {
        this._popperContent$ = this.medPopup.show(this.componentKey, this.medPopupData)?.subscribe(data => {
          this.medPopup.hideActives();
          this.medPopupClose.emit(data);
          this._updatePopper(false);
        });

        this._updatePopper(!this.medPopup.registerOfActives[this._hash]);
      });

    this._clickOutside$ = this.medPopup.clickOutside$
      .pipe(filter(data => this._popperInstance !== null
        && this.medPopup.registerOfActives[this._hash]
        && !this._isClickedInside(data)))
      .subscribe(() => {
        if (this.medPopup.hasMoreActives) {
          this.medPopup.setActive(this._hash, false);
        } else {
          this._updatePopper(false);
        }
      });
  }

  ngOnDestroy() {
    this._popperInstance?.destroy();
    this._clickOutside$?.unsubscribe();
    this._popperContent$?.unsubscribe();
    this.medPopup.deleteActive(this._hash);

    if (this.medPopupStyleClass) {
      this.medPopup.popupContentEl.classList.remove(this.medPopupStyleClass);
    }
  }

  private _updatePopper(enabled: boolean) {
    this.medPopup.setActive(this._hash, enabled);

    // Enable the event listeners
    this._popperInstance?.setOptions(options => ({
      ...options,
      modifiers: [
        ...(options.modifiers || []),
        { name: "eventListeners", enabled },
      ],
    }));

    if (enabled) {
      this.renderer.removeStyle(this.medPopup.popupContentEl, "display");
    } else {
      this.renderer.setStyle(this.medPopup.popupContentEl, "display", "none");
    }

    this._popperInstance?.update();
  }

  private _isClickedInside(target: EventTarget): boolean {
    return this.el.nativeElement.contains(target);
  }
}
